import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";
import { FormRecordProvider } from "../../../../common/controls/forms/FormRecordContext";
import FormExtras from "../../../../common/components/formExtras";
import { AuthorComposerCreateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { FieldAdornmentSlot } from "../../../../common/components/projectSlots";
import { Gender } from "../../../../api/graphql";
import { InputReference } from "../../../common/controls";
import { TaxIdType } from "../../../../api/graphql";
import { AuthorComposerTaxIdCustomField } from "../custom/AuthorComposerTaxIdCustomField";
import { EmailVerificationIndicator } from "../../../common/controls/EmailVerificationIndicator";
import { InputPhone } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { CountryCreate } from "../../../country/components/create/CountryCreate";
import { CountryCreateStore } from "../../../country/store";
import { InputDate } from "../../../common/controls";
import { FormControl } from "@mui/material";
import { ProvinceStateCreate } from "../../../provinceState/components/create/ProvinceStateCreate";
import { ProvinceStateCreateStore } from "../../../provinceState/store";
import { AuthorComposerIpiCustomField } from "../custom/AuthorComposerIpiCustomField";

export type AuthorComposerPublicCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  firstNameSelectedValue?: { displayId: string; id: string } | null;
  onFirstNameChange?: (value: string) => void;
  firstName: string;
  firstNameError?: string;
  lastNameSelectedValue?: { displayId: string; id: string } | null;
  onLastNameChange?: (value: string) => void;
  lastName: string;
  lastNameError?: string;
  genderError?: string;
  genderOptions: { id: string; displayId: string }[];
  gender: Gender | "";
  onGenderChange?: (value: Gender | "") => void;
  taxIdTypeError?: string;
  taxIdTypeOptions: { id: string; displayId: string }[];
  taxIdType: TaxIdType | "";
  onTaxIdTypeChange?: (value: TaxIdType | "") => void;
  taxIdSelectedValue?: { displayId: string; id: string } | null;
  onTaxIdChange?: (value: string) => void;
  taxId: string;
  taxIdError?: string;
  emailSelectedValue?: { displayId: string; id: string } | null;
  onEmailChange?: (value: string) => void;
  email: string;
  emailError?: string;
  phoneSelectedValue?: { displayId: string; id: string } | null;
  onPhoneChange?: (value: string) => void;
  phone: string;
  phoneError?: string;
  nationalitySelectedValue?: { displayId: string; id: string } | null;
  onNationalitySelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onNationalityChange?: (value: string) => void;
  nationalityOptions?: { displayId: string; id: string }[];
  nationalityError?: string;
  dateOfBirthSelectedValue?: { displayId: string; id: string } | null;
  onDateOfBirthChange?: (value: string | null) => void;
  dateOfBirth: string | null;
  dateOfBirthError?: string;
  streetAndNumberSelectedValue?: { displayId: string; id: string } | null;
  onStreetAndNumberChange?: (value: string) => void;
  streetAndNumber: string;
  streetAndNumberError?: string;
  floorSelectedValue?: { displayId: string; id: string } | null;
  onFloorChange?: (value: string) => void;
  floor: string;
  floorError?: string;
  suiteSelectedValue?: { displayId: string; id: string } | null;
  onSuiteChange?: (value: string) => void;
  suite: string;
  suiteError?: string;
  postalCodeSelectedValue?: { displayId: string; id: string } | null;
  onPostalCodeChange?: (value: string) => void;
  postalCode: string;
  postalCodeError?: string;
  countrySelectedValue?: { displayId: string; id: string } | null;
  onCountrySelected?: (value: { displayId: string; id: string } | null) => void;
  onCountryChange?: (value: string) => void;
  countryOptions?: { displayId: string; id: string }[];
  countryError?: string;
  provinceSelectedValue?: { displayId: string; id: string } | null;
  onProvinceSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onProvinceChange?: (value: string) => void;
  provinceOptions?: { displayId: string; id: string }[];
  provinceError?: string;
  citySelectedValue?: { displayId: string; id: string } | null;
  onCityChange?: (value: string) => void;
  city: string;
  cityError?: string;
  akaSelectedValue?: { displayId: string; id: string } | null;
  onAkaChange?: (value: string) => void;
  aka: string;
  akaError?: string;
  ipiSelectedValue?: { displayId: string; id: string } | null;
  onIpiChange?: (value: string) => void;
  ipi: string;
  ipiError?: string;
  authorComposerCreateStore: AuthorComposerCreateStore;
};

export const AuthorComposerPublicCreateForm: React.FC<AuthorComposerPublicCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      firstName = "",
      firstNameError,
      onFirstNameChange = () => {},
      lastName = "",
      lastNameError,
      onLastNameChange = () => {},
      genderError,
      genderOptions = [],
      gender = "" as Gender | "",
      onGenderChange = () => {},
      taxIdTypeError,
      taxIdTypeOptions = [],
      taxIdType = "" as TaxIdType | "",
      onTaxIdTypeChange = () => {},
      taxId = "",
      taxIdError,
      onTaxIdChange = () => {},
      email = "",
      emailError,
      onEmailChange = () => {},
      phone = "",
      phoneError,
      onPhoneChange = () => {},
      onNationalitySelected = () => {},
      nationalitySelectedValue = null,
      nationalityOptions = [],
      nationalityError,
      onNationalityChange = () => {},
      dateOfBirth = null,
      dateOfBirthError,
      onDateOfBirthChange = () => {},
      streetAndNumber = "",
      streetAndNumberError,
      onStreetAndNumberChange = () => {},
      floor = "",
      floorError,
      onFloorChange = () => {},
      suite = "",
      suiteError,
      onSuiteChange = () => {},
      postalCode = "",
      postalCodeError,
      onPostalCodeChange = () => {},
      onCountrySelected = () => {},
      countrySelectedValue = null,
      countryOptions = [],
      countryError,
      onCountryChange = () => {},
      onProvinceSelected = () => {},
      provinceSelectedValue = null,
      provinceOptions = [],
      provinceError,
      onProvinceChange = () => {},
      city = "",
      cityError,
      onCityChange = () => {},
      aka = "",
      akaError,
      onAkaChange = () => {},
      ipi = "",
      ipiError,
      onIpiChange = () => {},
      authorComposerCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormRecordProvider
              values={{
                firstName,
                lastName,
                gender,
                taxIdType,
                taxId,
                email,
                phone,
                nationality: nationalitySelectedValue,
                dateOfBirth,
                streetAndNumber,
                floor,
                suite,
                postalCode,
                country: countrySelectedValue,
                province: provinceSelectedValue,
                city,
                aka,
                ipi,
              }}
            >
              <FormExtras
                entity="AuthorComposer"
                mode="create"
                values={{
                  firstName,
                  lastName,
                  gender,
                  taxIdType,
                  taxId,
                  email,
                  phone,
                  nationality: nationalitySelectedValue,
                  dateOfBirth,
                  streetAndNumber,
                  floor,
                  suite,
                  postalCode,
                  country: countrySelectedValue,
                  province: provinceSelectedValue,
                  city,
                  aka,
                  ipi,
                }}
                options={{
                  nationality: nationalityOptions,
                  country: countryOptions,
                  province: provinceOptions,
                }}
                onPatch={(campo: string, valor: any) => {
                  switch (campo) {
                    case "firstName":
                      onFirstNameChange?.(valor);
                      break;
                    case "lastName":
                      onLastNameChange?.(valor);
                      break;
                    case "gender":
                      onGenderChange?.(valor);
                      break;
                    case "taxIdType":
                      onTaxIdTypeChange?.(valor);
                      break;
                    case "taxId":
                      onTaxIdChange?.(valor);
                      break;
                    case "email":
                      onEmailChange?.(valor);
                      break;
                    case "phone":
                      onPhoneChange?.(valor);
                      break;
                    case "nationality":
                      onNationalitySelected?.(valor);
                      break;
                    case "dateOfBirth":
                      onDateOfBirthChange?.(valor);
                      break;
                    case "streetAndNumber":
                      onStreetAndNumberChange?.(valor);
                      break;
                    case "floor":
                      onFloorChange?.(valor);
                      break;
                    case "suite":
                      onSuiteChange?.(valor);
                      break;
                    case "postalCode":
                      onPostalCodeChange?.(valor);
                      break;
                    case "country":
                      onCountrySelected?.(valor);
                      break;
                    case "province":
                      onProvinceSelected?.(valor);
                      break;
                    case "city":
                      onCityChange?.(valor);
                      break;
                    case "aka":
                      onAkaChange?.(valor);
                      break;
                    case "ipi":
                      onIpiChange?.(valor);
                      break;
                  }
                }}
              />
              <GroupContainer
                id="personalInformation"
                errorText={authorComposerCreateStore.getCardErrors(
                  "personalInformation"
                )}
                title={
                  langStore.groupAuthorComposerPublicPersonalInformationLabel
                }
                collapsible={true}
                expanded={authorComposerCreateStore.getCardState(
                  "personalInformation"
                )}
                toggleExpand={() =>
                  authorComposerCreateStore.toggleCardState(
                    "personalInformation"
                  )
                }
              >
                <FormControl fullWidth>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="firstName"
                    mode="create"
                    value={firstName}
                  >
                    <InputString
                      autoFocus
                      value={firstName}
                      error={Boolean(firstNameError)}
                      warning={
                        !firstNameError &&
                        Boolean(authorComposerCreateStore.warnings["firstName"])
                      }
                      helperText={
                        firstNameError ||
                        authorComposerCreateStore.warnings["firstName"]?.message
                      }
                      label={langStore.AuthorComposerFirstNameLabel}
                      onChange={(value: string) => onFirstNameChange(value)}
                      description={langStore.AuthorComposerFirstNameDescription}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="lastName"
                    mode="create"
                    value={lastName}
                  >
                    <InputString
                      value={lastName}
                      error={Boolean(lastNameError)}
                      warning={
                        !lastNameError &&
                        Boolean(authorComposerCreateStore.warnings["lastName"])
                      }
                      helperText={
                        lastNameError ||
                        authorComposerCreateStore.warnings["lastName"]?.message
                      }
                      label={langStore.AuthorComposerLastNameLabel}
                      onChange={(value: string) => onLastNameChange(value)}
                      description={langStore.AuthorComposerLastNameDescription}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="gender"
                    mode="create"
                    value={gender}
                  >
                    <InputReference
                      id="gender"
                      error={Boolean(genderError)}
                      helperText={
                        genderError ||
                        authorComposerCreateStore.warnings["gender"]?.message
                      }
                      label={langStore.AuthorComposerGenderLabel}
                      options={genderOptions}
                      nullText={langStore.optionalFieldHint}
                      selectedValue={
                        gender
                          ? {
                              id: gender,
                              displayId:
                                genderOptions.find(
                                  (o: { id: string }) => o.id === gender
                                )?.displayId || gender,
                            }
                          : null
                      }
                      onChange={() => {}}
                      onSelect={(
                        value: { id: string; displayId: string } | null
                      ) =>
                        onGenderChange(
                          (value?.id as Gender | "") || ("" as Gender | "")
                        )
                      }
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="taxIdType"
                    mode="create"
                    value={taxIdType}
                  >
                    <InputReference
                      id="taxIdType"
                      error={Boolean(taxIdTypeError)}
                      helperText={
                        taxIdTypeError ||
                        authorComposerCreateStore.warnings["taxIdType"]?.message
                      }
                      label={langStore.AuthorComposerTaxIdTypeLabel}
                      options={taxIdTypeOptions}
                      nullText={langStore.optionalFieldHint}
                      selectedValue={
                        taxIdType
                          ? {
                              id: taxIdType,
                              displayId:
                                taxIdTypeOptions.find(
                                  (o: { id: string }) => o.id === taxIdType
                                )?.displayId || taxIdType,
                            }
                          : null
                      }
                      onChange={() => {}}
                      onSelect={(
                        value: { id: string; displayId: string } | null
                      ) =>
                        onTaxIdTypeChange(
                          (value?.id as TaxIdType | "") ||
                            ("" as TaxIdType | "")
                        )
                      }
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="taxId"
                    mode="create"
                    value={taxId}
                  >
                    <AuthorComposerTaxIdCustomField
                      value={taxId}
                      error={Boolean(taxIdError)}
                      helperText={
                        taxIdError ||
                        authorComposerCreateStore.warnings["taxId"]?.message
                      }
                      {...{ optional: true }}
                      label={langStore.AuthorComposerTaxIdLabel}
                      description={langStore.AuthorComposerTaxIdDescription}
                      onChange={(value: string) => onTaxIdChange(value)}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="email"
                    mode="create"
                    value={email}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <InputString
                        value={email}
                        error={Boolean(emailError)}
                        warning={
                          !emailError &&
                          Boolean(authorComposerCreateStore.warnings["email"])
                        }
                        helperText={
                          emailError ||
                          authorComposerCreateStore.warnings["email"]?.message
                        }
                        label={langStore.AuthorComposerEmailLabel}
                        onChange={(value: string) => onEmailChange(value)}
                        description={langStore.AuthorComposerEmailDescription}
                      />
                      <EmailVerificationIndicator
                        email={email}
                        entityName="AuthorComposer"
                        validationMode={true}
                      />
                    </Box>
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="phone"
                    mode="create"
                    value={phone}
                  >
                    <InputPhone
                      value={phone}
                      error={Boolean(phoneError)}
                      helperText={
                        phoneError ||
                        authorComposerCreateStore.warnings["phone"]?.message
                      }
                      label={langStore.AuthorComposerPhoneLabel}
                      onChange={(value: string) => onPhoneChange(value)}
                      nullText={langStore.optionalFieldHint}
                      description={langStore.AuthorComposerPhoneDescription}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="nationality"
                    mode="create"
                    value={nationalitySelectedValue}
                  >
                    <InputReference
                      id="nationality"
                      label={langStore.AuthorComposerNationalityLabel}
                      error={Boolean(nationalityError)}
                      helperText={
                        nationalityError ||
                        authorComposerCreateStore.warnings["nationality"]
                          ?.message
                      }
                      selectedValue={nationalitySelectedValue}
                      onChange={(value: string) => onNationalityChange(value)}
                      onSelect={(
                        value: { displayId: string; id: string } | null
                      ) => onNationalitySelected(value)}
                      options={nationalityOptions}
                      description={
                        langStore.AuthorComposerNationalityDescription
                      }
                      nullText={langStore.optionalFieldHint}
                      canCreate={rootStore.authStore.hasPermissions(
                        "country_create"
                      )}
                      renderCreateDialog={(dialogProps) => (
                        <CreateEntityDialog
                          {...dialogProps}
                          createStore={(rootStore) =>
                            new CountryCreateStore(rootStore)
                          }
                          renderForm={(store) => (
                            <CountryCreate
                              countryCreateStore={store}
                              showToolbox={false}
                              showSaveFooter={false}
                            />
                          )}
                          saveMethod="saveNewCountry"
                          cancelMethod="cancelNewCountry"
                        />
                      )}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="dateOfBirth"
                    mode="create"
                    value={dateOfBirth}
                  >
                    <InputDate
                      value={dateOfBirth}
                      error={Boolean(dateOfBirthError)}
                      warning={
                        !dateOfBirthError &&
                        Boolean(
                          authorComposerCreateStore.warnings["dateOfBirth"]
                        )
                      }
                      helperText={
                        dateOfBirthError ||
                        authorComposerCreateStore.warnings["dateOfBirth"]
                          ?.message
                      }
                      label={langStore.AuthorComposerDateOfBirthLabel}
                      nullText={langStore.optionalFieldHint}
                      description={
                        langStore.AuthorComposerDateOfBirthDescription
                      }
                      onChange={(value: string | null | null) =>
                        onDateOfBirthChange(value)
                      }
                    />
                  </FieldAdornmentSlot>
                </FormControl>
              </GroupContainer>

              <GroupContainer
                id="address"
                errorText={authorComposerCreateStore.getCardErrors("address")}
                title={langStore.groupAuthorComposerPublicAddressLabel}
                collapsible={true}
                expanded={authorComposerCreateStore.getCardState("address")}
                toggleExpand={() =>
                  authorComposerCreateStore.toggleCardState("address")
                }
              >
                <FormControl fullWidth>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="streetAndNumber"
                    mode="create"
                    value={streetAndNumber}
                  >
                    <InputString
                      value={streetAndNumber}
                      error={Boolean(streetAndNumberError)}
                      warning={
                        !streetAndNumberError &&
                        Boolean(
                          authorComposerCreateStore.warnings["streetAndNumber"]
                        )
                      }
                      helperText={
                        streetAndNumberError ||
                        authorComposerCreateStore.warnings["streetAndNumber"]
                          ?.message
                      }
                      label={langStore.AuthorComposerStreetAndNumberLabel}
                      onChange={(value: string) =>
                        onStreetAndNumberChange(value)
                      }
                      nullText={langStore.optionalFieldHint}
                      description={
                        langStore.AuthorComposerStreetAndNumberDescription
                      }
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="floor"
                    mode="create"
                    value={floor}
                  >
                    <InputString
                      value={floor}
                      error={Boolean(floorError)}
                      warning={
                        !floorError &&
                        Boolean(authorComposerCreateStore.warnings["floor"])
                      }
                      helperText={
                        floorError ||
                        authorComposerCreateStore.warnings["floor"]?.message
                      }
                      label={langStore.AuthorComposerFloorLabel}
                      onChange={(value: string) => onFloorChange(value)}
                      nullText={langStore.optionalFieldHint}
                      description={langStore.AuthorComposerFloorDescription}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="suite"
                    mode="create"
                    value={suite}
                  >
                    <InputString
                      value={suite}
                      error={Boolean(suiteError)}
                      warning={
                        !suiteError &&
                        Boolean(authorComposerCreateStore.warnings["suite"])
                      }
                      helperText={
                        suiteError ||
                        authorComposerCreateStore.warnings["suite"]?.message
                      }
                      label={langStore.AuthorComposerSuiteLabel}
                      onChange={(value: string) => onSuiteChange(value)}
                      nullText={langStore.optionalFieldHint}
                      description={langStore.AuthorComposerSuiteDescription}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="postalCode"
                    mode="create"
                    value={postalCode}
                  >
                    <InputString
                      value={postalCode}
                      error={Boolean(postalCodeError)}
                      warning={
                        !postalCodeError &&
                        Boolean(
                          authorComposerCreateStore.warnings["postalCode"]
                        )
                      }
                      helperText={
                        postalCodeError ||
                        authorComposerCreateStore.warnings["postalCode"]
                          ?.message
                      }
                      label={langStore.AuthorComposerPostalCodeLabel}
                      onChange={(value: string) => onPostalCodeChange(value)}
                      nullText={langStore.optionalFieldHint}
                      description={
                        langStore.AuthorComposerPostalCodeDescription
                      }
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="country"
                    mode="create"
                    value={countrySelectedValue}
                  >
                    <InputReference
                      id="country"
                      label={langStore.AuthorComposerCountryLabel}
                      error={Boolean(countryError)}
                      helperText={
                        countryError ||
                        authorComposerCreateStore.warnings["country"]?.message
                      }
                      selectedValue={countrySelectedValue}
                      onChange={(value: string) => onCountryChange(value)}
                      onSelect={(
                        value: { displayId: string; id: string } | null
                      ) => onCountrySelected(value)}
                      options={countryOptions}
                      description={langStore.AuthorComposerCountryDescription}
                      nullText={langStore.optionalFieldHint}
                      canCreate={rootStore.authStore.hasPermissions(
                        "country_create"
                      )}
                      renderCreateDialog={(dialogProps) => (
                        <CreateEntityDialog
                          {...dialogProps}
                          createStore={(rootStore) =>
                            new CountryCreateStore(rootStore)
                          }
                          renderForm={(store) => (
                            <CountryCreate
                              countryCreateStore={store}
                              showToolbox={false}
                              showSaveFooter={false}
                            />
                          )}
                          saveMethod="saveNewCountry"
                          cancelMethod="cancelNewCountry"
                        />
                      )}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="province"
                    mode="create"
                    value={provinceSelectedValue}
                  >
                    <InputReference
                      id="province"
                      label={langStore.AuthorComposerProvinceLabel}
                      error={Boolean(provinceError)}
                      helperText={
                        provinceError ||
                        authorComposerCreateStore.warnings["province"]?.message
                      }
                      selectedValue={provinceSelectedValue}
                      onChange={(value: string) => onProvinceChange(value)}
                      onSelect={(
                        value: { displayId: string; id: string } | null
                      ) => onProvinceSelected(value)}
                      options={provinceOptions}
                      description={langStore.AuthorComposerProvinceDescription}
                      nullText={langStore.optionalFieldHint}
                      canCreate={rootStore.authStore.hasPermissions(
                        "provinceState_create"
                      )}
                      renderCreateDialog={(dialogProps) => (
                        <CreateEntityDialog
                          {...dialogProps}
                          createStore={(rootStore) =>
                            new ProvinceStateCreateStore(rootStore)
                          }
                          renderForm={(store) => (
                            <ProvinceStateCreate
                              provinceStateCreateStore={store}
                              showToolbox={false}
                              showSaveFooter={false}
                            />
                          )}
                          saveMethod="saveNewProvinceState"
                          cancelMethod="cancelNewProvinceState"
                        />
                      )}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="city"
                    mode="create"
                    value={city}
                  >
                    <InputString
                      value={city}
                      error={Boolean(cityError)}
                      warning={
                        !cityError &&
                        Boolean(authorComposerCreateStore.warnings["city"])
                      }
                      helperText={
                        cityError ||
                        authorComposerCreateStore.warnings["city"]?.message
                      }
                      label={langStore.AuthorComposerCityLabel}
                      onChange={(value: string) => onCityChange(value)}
                      nullText={langStore.optionalFieldHint}
                      description={langStore.AuthorComposerCityDescription}
                    />
                  </FieldAdornmentSlot>
                </FormControl>
              </GroupContainer>

              <GroupContainer
                id="professionalInformation"
                errorText={authorComposerCreateStore.getCardErrors(
                  "professionalInformation"
                )}
                title={
                  langStore.groupAuthorComposerPublicProfessionalInformationLabel
                }
                collapsible={true}
                expanded={authorComposerCreateStore.getCardState(
                  "professionalInformation"
                )}
                toggleExpand={() =>
                  authorComposerCreateStore.toggleCardState(
                    "professionalInformation"
                  )
                }
              >
                <FormControl fullWidth>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="aka"
                    mode="create"
                    value={aka}
                  >
                    <InputString
                      value={aka}
                      error={Boolean(akaError)}
                      warning={
                        !akaError &&
                        Boolean(authorComposerCreateStore.warnings["aka"])
                      }
                      helperText={
                        akaError ||
                        authorComposerCreateStore.warnings["aka"]?.message
                      }
                      label={langStore.AuthorComposerAkaLabel}
                      onChange={(value: string) => onAkaChange(value)}
                      nullText={langStore.optionalFieldHint}
                      description={langStore.AuthorComposerAkaDescription}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="ipi"
                    mode="create"
                    value={ipi}
                  >
                    <AuthorComposerIpiCustomField
                      value={ipi}
                      error={Boolean(ipiError)}
                      helperText={
                        ipiError ||
                        authorComposerCreateStore.warnings["ipi"]?.message
                      }
                      {...{ optional: true }}
                      label={langStore.AuthorComposerIpiLabel}
                      description={langStore.AuthorComposerIpiDescription}
                      onChange={(value: string) => onIpiChange(value)}
                    />
                  </FieldAdornmentSlot>
                </FormControl>
              </GroupContainer>
            </FormRecordProvider>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
